import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

@Component({
  selector: 'app-ikigai-plan-vida',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ikigai.component.html',
  styleUrls: ['./ikigai.component.scss']
})
export class IkigaiPlanVidaComponent implements OnInit {
  
  // Estado del componente
  currentStep: 'introduction' | 'passion' | 'mission' | 'vocation' | 'profession' | 'results' = 'introduction';
  
  // Datos del Ikigai
  ikigaiData: { [key: string]: { [key: string]: string } } = {
    passion: {
      whatYouLove: '',
      whatYouEnjoy: '',
      whatExcitesYou: ''
    },
    mission: {
      whatTheWorldNeeds: '',
      whatYouCanContribute: '',
      whatMakesADifference: ''
    },
    vocation: {
      whatYouAreGoodAt: '',
      whatYouExcelAt: '',
      whatSkillsYouHave: ''
    },
    profession: {
      whatYouCanBePaidFor: '',
      whatYouCanEarnFrom: '',
      whatMarketNeeds: ''
    }
  };
  
  // Preguntas para cada sección
  questions = {
    passion: [
      { id: 'whatYouLove', text: '¿Qué amas hacer?', placeholder: 'Describe las actividades que más disfrutas...' },
      { id: 'whatYouEnjoy', text: '¿Qué te hace sentir vivo?', placeholder: 'Menciona las experiencias que te llenan de energía...' },
      { id: 'whatExcitesYou', text: '¿Qué te emociona?', placeholder: 'Describe lo que te genera pasión y entusiasmo...' }
    ],
    mission: [
      { id: 'whatTheWorldNeeds', text: '¿Qué necesita el mundo?', placeholder: 'Identifica problemas o necesidades que observas...' },
      { id: 'whatYouCanContribute', text: '¿En qué puedes contribuir?', placeholder: 'Describe cómo puedes ayudar o aportar...' },
      { id: 'whatMakesADifference', text: '¿Qué marca la diferencia?', placeholder: 'Reflexiona sobre lo que realmente importa...' }
    ],
    vocation: [
      { id: 'whatYouAreGoodAt', text: '¿En qué eres bueno?', placeholder: 'Menciona tus habilidades y fortalezas...' },
      { id: 'whatYouExcelAt', text: '¿En qué sobresales?', placeholder: 'Describe tus talentos únicos...' },
      { id: 'whatSkillsYouHave', text: '¿Qué habilidades tienes?', placeholder: 'Lista tus competencias y capacidades...' }
    ],
    profession: [
      { id: 'whatYouCanBePaidFor', text: '¿Por qué te pueden pagar?', placeholder: 'Identifica servicios o productos que puedes ofrecer...' },
      { id: 'whatYouCanEarnFrom', text: '¿De qué puedes vivir?', placeholder: 'Describe fuentes de ingresos potenciales...' },
      { id: 'whatMarketNeeds', text: '¿Qué necesita el mercado?', placeholder: 'Reflexiona sobre demandas del mercado...' }
    ]
  };

  constructor(private router: Router) {}

  ngOnInit(): void {
    // Inicialización del componente
  }

  // Navegación entre pasos
  nextStep(): void {
    switch (this.currentStep) {
      case 'introduction':
        this.currentStep = 'passion';
        break;
      case 'passion':
        this.currentStep = 'mission';
        break;
      case 'mission':
        this.currentStep = 'vocation';
        break;
      case 'vocation':
        this.currentStep = 'profession';
        break;
      case 'profession':
        this.currentStep = 'results';
        break;
    }
  }

  previousStep(): void {
    switch (this.currentStep) {
      case 'passion':
        this.currentStep = 'introduction';
        break;
      case 'mission':
        this.currentStep = 'passion';
        break;
      case 'vocation':
        this.currentStep = 'mission';
        break;
      case 'profession':
        this.currentStep = 'vocation';
        break;
      case 'results':
        this.currentStep = 'profession';
        break;
    }
  }

  // Gestión de datos
  updateIkigaiData(section: string, field: string, value: string): void {
    if (this.ikigaiData[section]) {
      this.ikigaiData[section][field] = value;
    }
  }

  getIkigaiData(section: string, field: string): string {
    return this.ikigaiData[section]?.[field] || '';
  }

  // Validaciones
  canProceedFromSection(section: string): boolean {
    const sectionData = this.ikigaiData[section];
    if (!sectionData) return false;
    return Object.values(sectionData).some((value: string) => value.trim() !== '');
  }

  // Obtener el paso actual
  getCurrentStepTitle(): string {
    const titles = {
      'introduction': 'Introducción al Ikigai',
      'passion': 'Tu Pasión',
      'mission': 'Tu Misión',
      'vocation': 'Tu Vocación',
      'profession': 'Tu Profesión',
      'results': 'Tu Ikigai'
    };
    return titles[this.currentStep];
  }

  // Obtener el progreso
  getProgress(): number {
    const steps = ['passion', 'mission', 'vocation', 'profession'];
    const completedSteps = steps.filter(step => this.canProceedFromSection(step)).length;
    return (completedSteps / steps.length) * 100;
  }

  // Métodos auxiliares para el template
  hasPassionData(): boolean {
    return Object.values(this.ikigaiData['passion']).some(v => v.trim() !== '');
  }

  hasMissionData(): boolean {
    return Object.values(this.ikigaiData['mission']).some(v => v.trim() !== '');
  }

  hasVocationData(): boolean {
    return Object.values(this.ikigaiData['vocation']).some(v => v.trim() !== '');
  }

  hasProfessionData(): boolean {
    return Object.values(this.ikigaiData['profession']).some(v => v.trim() !== '');
  }

  // Método auxiliar para manejar el evento del textarea
  onTextareaChange(section: string, field: string, event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.updateIkigaiData(section, field, target.value);
  }

  // Finalizar proceso
  finishProcess(): void {
    console.log('Datos del Ikigai:', this.ikigaiData);
    // Aquí se podría guardar la información en el backend
  }

  // Reiniciar proceso
  restartProcess(): void {
    this.currentStep = 'introduction';
    this.ikigaiData = {
      passion: { whatYouLove: '', whatYouEnjoy: '', whatExcitesYou: '' },
      mission: { whatTheWorldNeeds: '', whatYouCanContribute: '', whatMakesADifference: '' },
      vocation: { whatYouAreGoodAt: '', whatYouExcelAt: '', whatSkillsYouHave: '' },
      profession: { whatYouCanBePaidFor: '', whatYouCanEarnFrom: '', whatMarketNeeds: '' }
    };
  }
}
